import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { AddToSavedListButton } from "@/components/saved-lists/AddToSavedListButton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import { SalonStockerHttpTypes } from "@repo/api-sdk";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { formatPrice } from "@/utils/money";
import { Package, X } from "lucide-react";
import type { FC } from "react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductDetailDialogHeader } from "./ProductDetailDialogHeader";
import { VariantOrderTab } from "./tabs";
import { useProductDetailDialogData } from "./useProductDetailDialogData";
import { useVariantGridData } from "./useVariantGridData";
import { VariantOrderFooter } from "./VariantOrderFooter";
import {
  getVariantTabConfig,
  isMultiVariantProductType
} from "./variantTabConfig";

export interface ProductDetailDialogProps {
  // Dialog control
  open: boolean;
  onOpenChange: (open: boolean) => void;

  // Static product data (from parent)
  sellerName: string;
  productName: string;
  thumbnail: string | null;

  // Offer ID for data fetching (single-offer mode)
  offerId: string;

  // Multi-variant mode (color_with_variants etc.)
  productId?: string;
  productType?: string;
  sellerId?: string;

  // Single-offer mode: the offer's product, so a claimed seller's trade price
  // can be fetched (the offer endpoint only has the retail one). KAN-343.
  pricingProductId?: string;

  // State
  isAddingToCart?: boolean;

  // Callbacks
  onAddToCart?: (offerId: string, quantity: number) => void;
  onQuickBuy?: (offerId: string, quantity: number) => void;
  onAddOffersToCart?: (items: { offerId: string; quantity: number }[]) => void;
  onQuickBuyOffers?: (items: { offerId: string; quantity: number }[]) => void;
}

const ProductDetailDialog: FC<ProductDetailDialogProps> = React.memo(
  (props) => {
    const {
      open,
      onOpenChange,
      sellerName,
      productName,
      thumbnail,
      offerId,
      productId,
      productType,
      sellerId,
      pricingProductId,
      isAddingToCart = false,
      onAddToCart,
      onQuickBuy,
      onAddOffersToCart,
      onQuickBuyOffers
    } = props;

    const { t } = useTranslation();

    const isVariantsMode =
      isMultiVariantProductType(productType) && !!productId && !!sellerId;

    // ── Single-offer mode state/data ──────────────────────────────────────────
    const [quantity, setQuantity] = useState(1);
    const { data, isLoading, isError, priceState } = useProductDetailDialogData(
      {
        offerId,
        productId: pricingProductId ?? productId,
        enabled: open && !isVariantsMode
      }
    );

    // ── Multi-variant mode state/data ─────────────────────────────────────────
    const [quantities, setQuantities] = useState<Record<string, number>>({});
    const {
      rows,
      inStockCount,
      isLoading: isVariantsLoading,
      isError: isVariantsError
    } = useVariantGridData({
      productId: productId ?? "",
      sellerId: sellerId ?? "",
      enabled: open && isVariantsMode
    });

    // Reset transient quantities whenever the dialog opens
    React.useEffect(() => {
      if (open) {
        setQuantity(1);
        setQuantities({});
      }
    }, [open]);

    const handleClose = () => onOpenChange(false);

    // ── Multi-variant derived values ──────────────────────────────────────────
    const selectedCount = useMemo(
      () => Object.values(quantities).filter((q) => q > 0).length,
      [quantities]
    );

    const totalFormatted = useMemo(() => {
      const total = rows.reduce(
        (sum, row) => sum + row.priceAmount * (quantities[row.variantId] ?? 0),
        0
      );
      return formatPrice(total);
    }, [rows, quantities]);

    const handleVariantQuantityChange = (variantId: string, qty: number) => {
      setQuantities((prev) => ({ ...prev, [variantId]: qty }));
    };

    const buildOrderItems = () =>
      rows
        .filter((row) => (quantities[row.variantId] ?? 0) > 0)
        .map((row) => ({
          offerId: row.offerId,
          quantity: quantities[row.variantId]
        }));

    const handleVariantsAddToCart = () =>
      onAddOffersToCart?.(buildOrderItems());

    const handleVariantsQuickBuy = () => onQuickBuyOffers?.(buildOrderItems());

    // The selected variant rows as saved-list lines (variant-level; locked seller).
    const buildSavedListItems =
      (): SalonStockerHttpTypes.StoreAddSavedListItem[] =>
        rows
          .filter((row) => (quantities[row.variantId] ?? 0) > 0)
          .map((row) => ({
            variant_id: row.variantId,
            seller_id: sellerId ?? "",
            quantity: quantities[row.variantId]
          }));

    const variantConfig = getVariantTabConfig(productType);
    const variantTabLabel = variantConfig
      ? t(variantConfig.labelKey, { defaultValue: variantConfig.labelDefault })
      : "Variants";
    const variantSearchPlaceholder = variantConfig
      ? t(variantConfig.searchPlaceholderKey, {
          defaultValue: variantConfig.searchPlaceholderDefault
        })
      : "Search...";

    // Default values for single-offer loading state
    const headerData = {
      priceCalculatedFormatted: data?.priceCalculatedFormatted ?? "$0.00",
      priceCalculatedAmount: data?.priceCalculatedAmount ?? 0,
      priceOriginalFormatted: data?.priceOriginalFormatted,
      savingsFormatted: data?.savingsFormatted,
      savingsPercentage: data?.savingsPercentage,
      status:
        data?.status ?? SalonStockerHttpTypes.StoreSellerOfferStatus.ACTIVE
    };

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          // Only the variant grid has a body to fill the height; the single-offer
          // dialog is just its header, so it sizes to that.
          //
          // Below `sm` the shared dialog primitive is `w-full` with square
          // corners - full-bleed, edge to edge. Inset it 1rem each side and round
          // it so it reads as a dialog sitting over the page, not a page.
          className={cn(
            "flex w-[calc(100%-2rem)] max-w-4xl flex-col gap-0 overflow-hidden rounded-lg p-0",
            isVariantsMode && "h-[90vh]"
          )}
        >
          {/* Radix labels the dialog from these. The visible header is built
              from the product and price, not a title element, so without them
              the dialog is unnamed to a screen reader and Radix logs a
              `DialogTitle` warning on every open. Same sr-only pattern as the
              voice dialogs. */}
          <DialogTitle className="sr-only">
            {`Add ${productName} from ${sellerName} to cart`}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isVariantsMode
              ? "Choose a quantity for each variant, then add the selection to your cart."
              : "Choose a quantity, then add it to your cart or buy it now."}
          </DialogDescription>
          {isVariantsMode ? (
            <>
              {/* Variant-mode header (buy controls live in the footer) */}
              <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white">
                <button
                  onClick={handleClose}
                  className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
                  aria-label="Close dialog"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="flex gap-4">
                  <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-xl bg-white shadow-lg">
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt={productName}
                        className="h-full w-full rounded-xl object-cover"
                      />
                    ) : (
                      <Package className="h-10 w-10 text-purple-600" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium italic text-yellow-100">
                      Order with
                    </p>
                    <p className="text-sm font-bold uppercase italic text-yellow-100">
                      {sellerName}
                    </p>
                    <TruncateTooltip
                      as="h2"
                      lines={2}
                      className="mt-1 pr-8 text-lg font-bold leading-tight lg:text-2xl"
                    >
                      {productName}
                    </TruncateTooltip>
                    {!isVariantsLoading && rows.length > 0 && (
                      <div className="mt-2 flex gap-2">
                        <Badge className="border-0 bg-white/20 text-white">
                          {rows.length} {variantTabLabel.toLowerCase()}
                        </Badge>
                        <Badge className="border-0 bg-white/20 text-white">
                          {inStockCount} in stock
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* The variant grid is the only content the dialog has. Supplier,
                  product and shipping tabs were "coming soon" placeholders and
                  are gone until there is real content for them, so there is
                  nothing to switch between and no tab bar. */}
              <div className="flex-1 overflow-hidden pt-3">
                <VariantOrderTab
                  rows={rows}
                  isLoading={isVariantsLoading}
                  isError={isVariantsError}
                  searchPlaceholder={variantSearchPlaceholder}
                  quantities={quantities}
                  onQuantityChange={handleVariantQuantityChange}
                  productThumbnail={thumbnail}
                />
              </div>

              {/* Footer */}
              <VariantOrderFooter
                selectedCount={selectedCount}
                totalFormatted={totalFormatted}
                isAddingToCart={isAddingToCart}
                onAddToCart={handleVariantsAddToCart}
                onQuickBuy={handleVariantsQuickBuy}
                addToListSlot={
                  <AddToSavedListButton
                    items={buildSavedListItems()}
                    disabled={selectedCount === 0}
                    className="h-9 gap-1.5 text-xs"
                  />
                }
              />
            </>
          ) : (
            <>
              {/* Single-offer header with inline buy controls */}
              <ProductDetailDialogHeader
                sellerName={sellerName}
                productName={productName}
                thumbnail={thumbnail}
                priceCalculatedFormatted={headerData.priceCalculatedFormatted}
                priceCalculatedAmount={headerData.priceCalculatedAmount}
                priceOriginalFormatted={headerData.priceOriginalFormatted}
                savingsFormatted={headerData.savingsFormatted}
                savingsPercentage={headerData.savingsPercentage}
                status={headerData.status}
                isLoading={isLoading}
                priceState={priceState}
                isAddingToCart={isAddingToCart}
                quantity={quantity}
                onQuantityChange={setQuantity}
                onAddToCart={() => onAddToCart?.(offerId, quantity)}
                onQuickBuy={() => onQuickBuy?.(offerId, quantity)}
                onClose={handleClose}
                addToListSlot={
                  data ? (
                    <>
                      <FavoriteButton
                        variantId={data.variantId}
                        sellerId={data.sellerId}
                        source="pdp"
                        disabled={isLoading}
                        className="h-9 w-9 bg-white/90 hover:bg-white"
                      />
                      <AddToSavedListButton
                        items={[
                          {
                            variant_id: data.variantId,
                            seller_id: data.sellerId,
                            quantity
                          }
                        ]}
                        disabled={isLoading}
                        className="h-9 gap-1.5 bg-white/90 text-xs text-gray-900 hover:bg-white"
                      />
                    </>
                  ) : null
                }
              />

              {/* Error state */}
              {isError && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                  <div className="text-center">
                    <p className="text-sm font-medium text-red-600">
                      Failed to load offer details
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Please try again later
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  }
);

ProductDetailDialog.displayName = "ProductDetailDialog";

export { ProductDetailDialog };
